/* SPDX-License-Identifier: Apache-2.0 */
/* SPDX-FileCopyrightText: 2021-2023 OKTET Labs Ltd. */
import { skipToken } from '@reduxjs/toolkit/query';

import { analyticsEventNames, trackEvent } from '@/bublik/features/analytics';
import {
	useGetLogJsonQuery,
	useGetLogUrlByResultIdQuery,
	useGetRunDetailsQuery
} from '@/services/bublik-api';
import {
	LogTableContext,
	LogTableContextProvider,
	LogMetaContextProvider,
	SessionLoading,
	SessionRoot
} from '@/bublik/features/session-log';
import { cn, RunRunning } from '@/shared/tailwind-ui';
import { BublikEmptyState, BublikErrorState } from '@/bublik/features/ui-state';
import { RUN_STATUS } from '@/shared/types';

import { useIsLogLegacy, useLogPage } from '../../hooks';

const highlightRow = (el: HTMLElement) => {
	el.classList.add('animate-highlight-row');
	el.addEventListener(
		'animationend',
		() => el.classList.remove('animate-highlight-row'),
		{ once: true }
	);
};

export interface LogFrameErrorProps {
	error: unknown;
}

export const LogFrameError = ({ error = {} }: LogFrameErrorProps) => {
	return <BublikErrorState error={error} iconSize={48} />;
};

export const LogFrameEmpty = () => {
	return (
		<BublikEmptyState
			title="No log found"
			description="No log data is available for selected result"
		/>
	);
};

export function LogPickerContainer() {
	const { isLegacyLog } = useIsLogLegacy();
	const {
		isShowingRunLog,
		focusId,
		runId,
		lineNumber,
		setLineNumber,
		page,
		setPage
	} = useLogPage();

	if (!runId) {
		return <BublikEmptyState title="No data" description="Run ID is missing" />;
	}

	const handlePageClick = (_: string, page: number) => {
		trackEvent(analyticsEventNames.logPageChange, {
			page
		});

		setPage(page);
	};

	const handleLineNumberClick = (id: string, clickNumber: number) => {
		const clickedId = `${id}_${clickNumber}`;
		const el = document.getElementById(clickedId);

		if (el) highlightRow(el);
		if (clickedId === lineNumber) return;

		trackEvent(analyticsEventNames.logLineBookmark, {
			action: 'save_line'
		});

		setLineNumber(clickedId);
	};

	const handleLogTableMount: LogTableContext['onLogTableMount'] = (
		_id,
		table
	) => {
		if (!lineNumber) return;

		// 1. Expand parent rows before we can scroll to row
		const row = table.getRowModel().rowsById[lineNumber];

		if (!row) return console.log('No row found with id', lineNumber);

		const parentRowsIds = row
			.getParentRows()
			.filter((row) => Array.isArray(row.original.children))
			.map((row) => row.id);

		parentRowsIds.forEach((rowId) => table.getRow(rowId).toggleExpanded(true));

		setTimeout(() => {
			const el = document.getElementById(lineNumber);

			if (!el) return;

			highlightRow(el);

			el.scrollIntoView({ block: 'start', inline: 'start' });
		}, 0);
	};

	return !isLegacyLog ? (
		<LogTableContextProvider
			onLineNumberClick={handleLineNumberClick}
			onLogTableMount={handleLogTableMount}
			onPageClick={handlePageClick}
		>
			<JsonLog
				runId={runId}
				focusId={focusId}
				page={page}
				isShowingRunLog={isShowingRunLog}
			/>
		</LogTableContextProvider>
	) : (
		<HtmlLog
			runId={runId}
			focusId={focusId}
			isShowingRunLog={isShowingRunLog}
		/>
	);
}

export interface HtmlLogProps {
	runId: string;
	focusId?: string | number | null;
	isShowingRunLog: boolean;
}

const HtmlLog = (props: HtmlLogProps) => {
	const { runId, focusId, isShowingRunLog } = props;

	const idToLoad =
		isShowingRunLog && runId
			? parseInt(runId)
			: focusId
			? parseInt(focusId.toString())
			: undefined;

	const { data, error, isLoading } = useGetLogUrlByResultIdQuery(
		idToLoad ?? skipToken
	);

	if (isLoading) return null;

	if (error) return <LogFrameError error={error} />;

	if (!data) return null;

	return (
		<iframe
			title="log"
			src={data.url}
			className="w-full h-full pl-2 border-none rounded-b-md"
		/>
	);
};

export interface JsonLogProps {
	runId: string;
	focusId?: string | number | null;
	page?: string | number | null;
	isShowingRunLog?: boolean;
}

const JsonLog = (props: JsonLogProps) => {
	const { runId, focusId, isShowingRunLog, page } = props;

	const idToFetch = isShowingRunLog
		? { id: runId }
		: focusId
		? { id: focusId, page }
		: skipToken;

	const { data, isLoading, isFetching, error } = useGetLogJsonQuery(idToFetch);
	const {
		data: details,
		isLoading: detailsLoading,
		error: detailsError
	} = useGetRunDetailsQuery(runId ?? skipToken);

	if (isLoading || detailsLoading) {
		return <SessionLoading />;
	}

	if (details?.conclusion === RUN_STATUS.Running) {
		return (
			<div className="flex justify-center items-center w-full h-full">
				<RunRunning />
			</div>
		);
	}

	if (error || detailsError) {
		return <LogFrameError error={error || detailsError} />;
	}

	if (!data || !details) {
		return <LogFrameEmpty />;
	}

	return (
		<div
			key={`${runId}_${focusId}_${page}_${isFetching}`}
			className={cn(isFetching && 'opacity-40 pointer-events-none', 'p-4')}
		>
			<LogMetaContextProvider runDetails={details}>
				<SessionRoot root={data} />
			</LogMetaContextProvider>
		</div>
	);
};
